/**
 * Quiz Block
 * Interactive quiz with progress tracking, scoring, and results.
 */

import { registerBlockRenderer } from '../registry';
import { escapeHtml, createBlockWrapper } from '../utils';

// =============================================================================
// TYPES
// =============================================================================

interface QuizQuestion {
  question: string;
  options: string[];
  correct: number;
  explanation?: string;
}

interface QuizData {
  title?: string;
  questions: QuizQuestion[];
  passingScore?: number;
}

// =============================================================================
// TYPE VALIDATION
// =============================================================================

function isValidQuizData(data: unknown): data is QuizData {
  if (!data || typeof data !== 'object') return false;
  const d = data as Record<string, unknown>;

  // Must have questions array
  if (!Array.isArray(d.questions) || d.questions.length === 0) return false;

  // Validate each question
  for (const q of d.questions) {
    if (!q || typeof q !== 'object') return false;
    const question = q as Record<string, unknown>;
    if (typeof question.question !== 'string') return false;
    if (!Array.isArray(question.options) || question.options.length < 2) return false;
    if (typeof question.correct !== 'number') return false;
  }

  return true;
}

// =============================================================================
// QUIZ STATE MANAGEMENT
// =============================================================================

interface QuizState {
  currentQuestion: number;
  answers: (number | null)[];
  showingResults: boolean;
}

// =============================================================================
// RENDER FUNCTIONS
// =============================================================================

function renderQuestionView(
  data: QuizData,
  state: QuizState,
  wrapper: HTMLElement,
  onStateChange: () => void
): void {
  const q = data.questions[state.currentQuestion]!;
  const totalQuestions = data.questions.length;
  const progress = ((state.currentQuestion + 1) / totalQuestions) * 100;

  let html = '';
  if (data.title) {
    html += `<h3 class="pms-quiz-title">${escapeHtml(data.title)}</h3>`;
  }

  html += `
    <div class="pms-quiz-header">
      <span class="pms-quiz-progress">Question ${state.currentQuestion + 1} of ${totalQuestions}</span>
    </div>
    <div class="pms-quiz-progress-bar">
      <div class="pms-quiz-progress-fill" style="width: ${progress}%"></div>
    </div>
    <div class="pms-quiz-question">${escapeHtml(q.question)}</div>
    <div class="pms-quiz-options">
  `;

  q.options.forEach((option, index) => {
    const isSelected = state.answers[state.currentQuestion] === index;
    const letter = String.fromCharCode(65 + index); // A, B, C, D
    html += `
      <button class="pms-quiz-option${isSelected ? ' pms-selected' : ''}" data-index="${index}">
        <span class="pms-quiz-option-marker">${letter}</span>
        <span class="pms-quiz-option-text">${escapeHtml(option)}</span>
      </button>
    `;
  });

  html += '</div>';

  // Navigation
  html += '<div class="pms-quiz-nav">';
  if (state.currentQuestion > 0) {
    html += '<button class="pms-quiz-nav-btn" data-nav="prev">Previous</button>';
  }
  if (state.currentQuestion < totalQuestions - 1) {
    html += '<button class="pms-quiz-nav-btn" data-nav="next">Next</button>';
  } else {
    html += '<button class="pms-quiz-nav-btn" data-nav="submit">See Results</button>';
  }
  html += '</div>';

  wrapper.innerHTML = html;

  // Event handlers
  wrapper.querySelectorAll('.pms-quiz-option').forEach(btn => {
    btn.addEventListener('click', () => {
      const index = parseInt((btn as HTMLElement).dataset.index || '0', 10);
      state.answers[state.currentQuestion] = index;
      onStateChange();
    });
  });

  wrapper.querySelectorAll('[data-nav]').forEach(btn => {
    btn.addEventListener('click', () => {
      const nav = (btn as HTMLElement).dataset.nav;
      if (nav === 'prev' && state.currentQuestion > 0) {
        state.currentQuestion--;
        onStateChange();
      } else if (nav === 'next' && state.currentQuestion < totalQuestions - 1) {
        state.currentQuestion++;
        onStateChange();
      } else if (nav === 'submit') {
        state.showingResults = true;
        onStateChange();
      }
    });
  });
}

function renderResultsView(
  data: QuizData,
  state: QuizState,
  wrapper: HTMLElement,
  onStateChange: () => void,
  onAction?: (value: string) => void
): void {
  const totalQuestions = data.questions.length;
  const correctCount = state.answers.filter(
    (a, i) => a === data.questions[i]?.correct
  ).length;
  const score = Math.round((correctCount / totalQuestions) * 100);
  const passingScore = data.passingScore || 70;
  const passed = score >= passingScore;

  let html = '';
  if (data.title) {
    html += `<h3 class="pms-quiz-title">${escapeHtml(data.title)}</h3>`;
  }

  html += `
    <div class="pms-quiz-results">
      <div class="pms-quiz-results-title">${passed ? 'Congratulations!' : 'Quiz Complete'}</div>
      <div class="pms-quiz-score">
        <span class="pms-quiz-score-value">${score}%</span>
        <span class="pms-quiz-score-label">${correctCount} of ${totalQuestions} correct</span>
      </div>
      <div class="pms-quiz-breakdown">
        <div class="pms-quiz-breakdown-title">Your Answers</div>
  `;

  data.questions.forEach((q, i) => {
    const userAnswer = state.answers[i];
    const isCorrect = userAnswer === q.correct;
    const icon = isCorrect ? '✓' : '✗';

    html += `
      <div class="pms-quiz-breakdown-item">
        <span class="pms-quiz-breakdown-icon ${isCorrect ? 'pms-correct' : 'pms-incorrect'}">${icon}</span>
        <div class="pms-quiz-breakdown-content">
          <div class="pms-quiz-breakdown-question">${escapeHtml(q.question)}</div>
          <div class="pms-quiz-breakdown-answer">
            Your answer: <strong>${userAnswer !== null && userAnswer !== undefined ? escapeHtml(q.options[userAnswer] || 'None') : 'Not answered'}</strong>
            ${!isCorrect ? `<br>Correct: <strong>${escapeHtml(q.options[q.correct] || '')}</strong>` : ''}
          </div>
          ${q.explanation ? `<div class="pms-quiz-explanation">${escapeHtml(q.explanation)}</div>` : ''}
        </div>
      </div>
    `;
  });

  html += `
      </div>
      <div class="pms-quiz-action">
        <button class="pms-quiz-nav-btn" data-nav="restart">Try Again</button>
      </div>
    </div>
  `;

  wrapper.innerHTML = html;

  // Emit score action
  if (onAction) {
    onAction(`quiz:complete:${score}`);
  }

  // Restart handler
  wrapper.querySelector('[data-nav="restart"]')?.addEventListener('click', () => {
    state.currentQuestion = 0;
    state.answers.fill(null);
    state.showingResults = false;
    onStateChange();
  });
}

// =============================================================================
// MAIN RENDER FUNCTION
// =============================================================================

function renderQuiz(
  data: QuizData,
  container: HTMLElement,
  onAction?: (value: string) => void
): void {
  const wrapper = createBlockWrapper('quiz');

  const state: QuizState = {
    currentQuestion: 0,
    answers: new Array(data.questions.length).fill(null),
    showingResults: false,
  };

  function render(): void {
    if (state.showingResults) {
      renderResultsView(data, state, wrapper, render, onAction);
    } else {
      renderQuestionView(data, state, wrapper, render);
    }
  }

  render();
  container.appendChild(wrapper);
}

// =============================================================================
// REGISTRATION
// =============================================================================

registerBlockRenderer('quiz', (data: unknown, container: HTMLElement, onAction?: (value: string) => void) => {
  if (!isValidQuizData(data)) {
    console.warn('[quiz] Invalid data:', data);
    const wrapper = createBlockWrapper('quiz');
    wrapper.innerHTML = '<div class="pms-block-error">Invalid quiz data - requires questions array</div>';
    container.appendChild(wrapper);
    return;
  }

  renderQuiz(data, container, onAction);
});
